/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { computed } from '@ember/object';
import { isNone } from '@ember/utils';
import operators, { comparisonOperators } from 'cio/utils/operators';
import AttributeCondition, {
  isBinaryOperator,
  isRelativeTimestampOperator,
  isAbsoluteTimestampOperator,
  isTwoValueOperator,
  canSelectComparisonOperator,
} from 'cio/app-models/conditions/attribute';
import CampaignModel from 'cio/models/campaign';
import { DataSourceLabelVariant } from 'cio/utils/data-source-label';
import CioConditionViewerBase, { CioConditionViewerBaseArgs } from 'cio/components/cio-condition-viewer-base';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import ObjectAttributeCondition from 'cio/app-models/conditions/object-attribute';
import { ConditionType } from 'cio/utils/condition-types';

interface CioConditionViewerAttributeArgs extends CioConditionViewerBaseArgs {
  campaign?: CampaignModel;
  condition: AttributeCondition | EventAttributeCondition | DeviceAttributeCondition | ObjectAttributeCondition;
  fieldClass?: string;
  operatorClass?: string;
  showName?: boolean;
  showBlank?: boolean;
  stripOperatorIs?: boolean;
  tagName?: string;
  isEventAttribute?: boolean;
  depth?: number;
  labelVariant?: DataSourceLabelVariant;
  labelTruncate?: boolean;
  labelClasses?: string;
  attributeSource?: string;
  containerClasses?: string;
}

export default class CioConditionViewerAttribute extends CioConditionViewerBase<CioConditionViewerAttributeArgs> {
  // We may invoke this component with tagName: ''
  // (see: visual-workflow-builder/actions/action-item)
  // and in those cases we want to disable the warning from ember-test-selectors
  supportsDataTestProperties = true;

  get tagName() {
    return this.args.tagName ?? 'div';
  }

  @computed('args.showBlank')
  get showBlank() {
    return this.args.showBlank ?? false;
  }

  get stripOperatorIs() {
    return this.args.stripOperatorIs ?? false;
  }

  get showName() {
    return this.args.showName ?? true;
  }

  get dataLabelType() {
    let { isEventAttribute, campaign, condition } = this.args;
    if (isEventAttribute === true) {
      return campaign?.isWebhookTriggered === true ? 'trigger_attribute' : 'event_attribute';
    }
    if (condition.type === ConditionType.ObjectAttribute || condition.type === ConditionType.RelationshipAttribute) {
      return condition.type;
    }
    return 'attribute';
  }

  get containerClasses() {
    return this.args.containerClasses ?? '';
  }

  @computed('args.condition')
  get condition() {
    return this.args.condition;
  }

  @computed('condition.operator', 'stripOperatorIs')
  get operatorLabel() {
    let value = this.condition.operator;
    let result = operators.findBy('value', value);

    if (isNone(result)) {
      return value;
    }

    let { label } = result as { label: string; value: string };

    if (this.stripOperatorIs) {
      label = label.replace('is a', '');
      label = label.replace('is ', '');
    }

    return label;
  }

  @computed('condition.operator')
  get isBinaryOperator() {
    return isBinaryOperator(this.condition.operator);
  }

  @computed('condition.operator')
  get isRelativeTimestampOperator() {
    return isRelativeTimestampOperator(this.condition.operator);
  }

  @computed('condition.operator')
  get isTwoValueOperator() {
    return isTwoValueOperator(this.condition.operator);
  }

  @computed('condition.{operator,comparisonOperator}')
  get isComparisonOperator() {
    return canSelectComparisonOperator(this.condition.operator) && this.condition.comparisonOperator;
  }

  @computed('isComparisonOperator', 'condition.comparisonOperator')
  get comparisonOperatorLabel() {
    if (!this.isComparisonOperator) {
      return false;
    }

    let operator = comparisonOperators.find((op) => op.value === this.condition.comparisonOperator);

    if (operator?.value === 'event_attribute' && this.args.campaign?.isWebhookTriggered) {
      return "the current person's trigger attribute";
    }

    return operator?.label;
  }

  @computed('condition.operator')
  get shouldFormatTimestamp() {
    let { operator } = this.condition;
    return isAbsoluteTimestampOperator(operator) || operator === 'timestamp_bw';
  }
}
